import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* ============================================================================
   Utils
============================================================================ */

function assertHTMLElement<T extends HTMLElement>(
    el: HTMLElement | null,
    id: string,
    ctor?: new (...args: any[]) => T
): T {
    if (!el) throw new Error(`Element with id "${id}" not found.`);
    if (ctor && !(el instanceof ctor)) {
        throw new Error(`Element with id "${id}" is not the expected type.`);
    }
    return el as T;
}

/* ============================================================================
   Video Switcher
============================================================================ */

function waitForFirstFrame(v: HTMLVideoElement): Promise<void> {
    if (v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        v.addEventListener("loadeddata", () => resolve(), { once: true });
    });
}

function assertVideo(el: HTMLElement | null, id: string): HTMLVideoElement {
    if (!(el instanceof HTMLVideoElement)) {
        throw new Error(
            `Element with id "${id}" is not an HTMLVideoElement or was not found.`
        );
    }
    return el;
}

function initVideoSwitcher(): void {
    const v1 = document.getElementById("phoneVideo1");
    const v2 = document.getElementById("phoneVideo2");

    const video1 = assertVideo(v1, "phoneVideo1");
    const video2 = assertVideo(v2, "phoneVideo2");
    const logo = document.getElementById("logoSplash") as HTMLDivElement;

    function showV1(): void {
        video2.pause();

        video1.currentTime = 0;
        void video1.play();

        video1.classList.remove("opacity-0");
        video1.classList.add("opacity-100");

        video2.classList.remove("opacity-100");
        video2.classList.add("opacity-0");
    }

    function showV2(): void {
        video1.pause();

        video2.currentTime = 0;
        void video2.play();

        video2.classList.remove("opacity-0");
        video2.classList.add("opacity-100");

        video1.classList.remove("opacity-100");
        video1.classList.add("opacity-0");
    }

    video1.addEventListener("ended", showV2);
    video2.addEventListener("ended", showV1);

    const observer = new IntersectionObserver(
        async (entries) => {
            const entry = entries[0];
            if (!entry.isIntersecting) return;

            observer.disconnect();
            video1.muted = true;
            video1.playsInline = true;
            video1.preload = "auto";

            video1.load();

            await waitForFirstFrame(video1);

            setTimeout(() => {
                logo.classList.remove("opacity-100");
                logo.classList.add("opacity-0");
            }, 800);

            showV1();
        },
        { threshold: 0.65 }
    );

    video1.classList.add("opacity-0");
    video2.classList.add("opacity-0");
    logo.classList.add("opacity-100");

    observer.observe(logo);
}

/* ============================================================================
   Features Progress + Active Item
============================================================================ */
function initFeatureHighlight(): void {
    const sectionEl = document.getElementById("features");
    const progressBarEl = document.getElementById("progressBarV");

    function assertHTMLElement(
        el: HTMLElement | null,
        id: string
    ): HTMLElement {
        if (!el) {
            throw new Error(`Element with id "${id}" not found.`);
        }
        return el;
    }

    const section = assertHTMLElement(
        sectionEl as HTMLElement | null,
        "features"
    );

    gsap.from(".features-header", {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
            trigger: "#features",
            start: "top 90%",
        },
    });

    const progressBar = assertHTMLElement(
        progressBarEl as HTMLElement | null,
        "progressBarV"
    );

    gsap.to(progressBar, {
        scaleY: 1,
        ease: "none",
        scrollTrigger: {
            trigger: section,
            start: "top top",
            end: "bottom bottom",
            scrub: true,
        },
    });

    let lastActiveIndex = -1;

    const featureItems =
        document.querySelectorAll<HTMLElement>(".feature-item");

    function setActive(index: number) {
        if (index === lastActiveIndex) return;
        lastActiveIndex = index;

        featureItems.forEach((item, i) => {
            const indexEl = item.querySelector<HTMLElement>(".text-5xl");
            if (!indexEl) return;

            if (i === index) {
                indexEl.classList.remove("text-gray-300");
                indexEl.classList.add("text-rose-500");

                gsap.fromTo(
                    indexEl,
                    { scale: 0.9, opacity: 0.6 },
                    { scale: 1, opacity: 1, duration: 0.4, ease: "power2.out" }
                );
            } else {
                indexEl.classList.add("text-gray-300");
                indexEl.classList.remove("text-rose-500");
            }
        });
    }

    featureItems.forEach((item, i) => {
        ScrollTrigger.create({
            trigger: item,
            start: "top 50%",
            end: "bottom 50%",
            onEnter: () => setActive(i),
            onEnterBack: () => setActive(i),
        });
    });

    featureItems.forEach((item) => {
        gsap.fromTo(
            item,
            { opacity: 0, y: 60 },
            {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: item,
                    start: "top 90%",
                },
            }
        );
    });
}

const initHowToSectionAnimations = () => {
    gsap.from(".js-how-header", {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
            trigger: ".js-how",
            start: "top 75%",
        },
    });

    gsap.utils.toArray<HTMLElement>(".js-how-step").forEach((step) => {
        const number = step.querySelector<HTMLElement>("div:first-child");
        const content = step.querySelector<HTMLElement>("div:last-child");

        if (!number || !content) {
            return;
        }

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: step,
                start: "top 80%",
            },
        });

        tl.from(number, {
            opacity: 0,
            y: 30,
            duration: 0.6,
            ease: "power3.out",
        }).from(
            Array.from(content.children) as HTMLElement[],
            {
                opacity: 0,
                y: 20,
                duration: 0.6,
                ease: "power3.out",
                stagger: 0.12,
            },
            "-=0.3"
        );
    });
};

const initForWhoSectionAnimations = () => {
    gsap.from(".js-forwho-header", {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
            trigger: ".js-forwho",
            start: "top 75%",
        },
    });

    gsap.utils.toArray<HTMLElement>(".js-forwho-item").forEach((item) => {
        const parts = item.children;

        if (parts.length < 2) {
            return;
        }

        const left = parts[0] as HTMLElement;
        const right = parts[1] as HTMLElement;

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: item,
                start: "top 80%",
            },
        });

        tl.from(left, {
            opacity: 0,
            x: -40,
            duration: 0.8,
            ease: "power3.out",
        }).from(
            right,
            {
                opacity: 0,
                x: 40,
                duration: 0.8,
                ease: "power3.out",
            },
            "-=0.4"
        );
    });
};

const initTestimonialsSectionAnimations = () => {
    gsap.from(".js-testimonials-left", {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
            trigger: ".js-testimonials",
            start: "top 75%",
        },
    });

    gsap.from(".js-testimonials-line", {
        scaleX: 0,
        opacity: 0,
        transformOrigin: "right center",
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
            trigger: ".js-testimonials-line",
            start: "top 85%",
        },
    });

    // Right column quotes (stagger)
    gsap.from(".js-testimonial-item", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.2,
        scrollTrigger: {
            trigger: ".js-testimonials-right",
            start: "top 80%",
        },
    });
};

const initCtaSectionAnimations = () => {
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: ".js-cta",
            start: "top 80%",
            toggleActions: "play none none reverse",
        },
    });

    gsap.set(".js-cta-title, .js-cta-text, .js-cta-buttons", {
        opacity: 0,
        y: 40,
    });

    tl.to(".js-cta-title", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
    })
        .to(
            ".js-cta-text",
            {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
            },
            "-=0.5"
        )
        .to(
            ".js-cta-buttons",
            {
                opacity: 1,
                y: 0,
                duration: 0.7,
                ease: "power3.out",
            },
            "-=0.4"
        );
};

const initStatsSectionAnimations = () => {
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: ".js-stats-header",
            start: "top 80%",
            toggleActions: "play none none reverse",
        },
    });

    gsap.set(".js-stats-header > *", {
        opacity: 0,
        y: 40,
    });

    tl.to(".js-stats-header > *", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.15,
    });
};

function initHeroSectionAnimations() {
    const hero = document.querySelector<HTMLElement>(".hero");
    if (!hero) return;

    const titleSpans = hero.querySelectorAll<HTMLElement>(".hero-title span");
    const titleH1 = hero.querySelectorAll<HTMLElement>(".hero-title2");
    const desc = hero.querySelector<HTMLElement>(".hero-desc");
    const buttons = hero.querySelectorAll<HTMLAnchorElement>(".hero-buttons a");
    const logo = hero.querySelector<SVGElement>("svg");

    const isMobile = window.innerWidth < 768;

    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: hero,
            start: "top 80%",
            toggleActions: "play none none reverse",
            once: true,
        },
        defaults: {
            ease: "power3.out",
            duration: 0.8,
        },
    });

    if (logo) {
        tl.from(logo, { opacity: 0, y: 40 }, 0.1);
    }

    if (titleSpans.length) {
        tl.from(
            titleSpans,
            { opacity: 0, y: 40, stagger: isMobile ? 0 : 0.15 },
            0.1
        );
    }

    if (titleH1.length) {
        tl.from(
            titleH1,
            { opacity: 0, y: 40, stagger: isMobile ? 0 : 0.15 },
            0.1
        );
    }

    if (desc) {
        tl.from(desc, { opacity: 0, y: 20 }, 0.4);
    }

    if (buttons.length) {
        tl.from(buttons, { opacity: 0, y: 20, stagger: 0.1 }, 0.6);
    }
}
const animateCounter = (
    el: HTMLElement,
    target: number,
    duration: number = 1500
): void => {
    let start: number | null = null;
    function step(timestamp: number) {
        if (start === null) start = timestamp;
        const progress = timestamp - start;
        const current = Math.min(
            Math.ceil((progress / duration) * target),
            target
        );
        el.innerText = current.toString();
        if (progress < duration) {
            requestAnimationFrame(step);
        } else {
            el.innerText = target.toString();
        }
    }
    requestAnimationFrame(step);
};

const initStatCounters = () => {
    const statsSection = document.getElementById("stats");
    let animated = false;
    if (statsSection) {
        const observer = new IntersectionObserver(
            (entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !animated) {
                        document
                            .querySelectorAll<HTMLElement>(".counter")
                            .forEach((counter) => {
                                const targetAttr =
                                    counter.getAttribute("data-target");
                                const target = targetAttr
                                    ? parseInt(targetAttr, 10)
                                    : 0;
                                animateCounter(counter, target, 1500);
                            });
                        animated = true;
                        obs.disconnect();
                    }
                });
            },
            { threshold: 0.5 }
        );
        observer.observe(statsSection);
    }
};

const initPhoneModelAnimations = () => {
    const phone = document.querySelector(".hero-phone");
    const tl = gsap.timeline();

    tl.fromTo(
        phone,
        {
            y: 80,
            opacity: 0,
            rotateX: 8,
            rotateY: -8,
            scale: 0.95,
        },
        {
            y: 0,
            opacity: 1,
            rotateX: 0,
            rotateY: 0,
            scale: 1,
            duration: 1.2,
            ease: "power3.out",
        }
    );

    tl.to(phone, {
        y: -5,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
    });
};

export {
    initVideoSwitcher,
    initFeatureHighlight,
    initHowToSectionAnimations,
    initForWhoSectionAnimations,
    initTestimonialsSectionAnimations,
    initCtaSectionAnimations,
    initStatsSectionAnimations,
    initHeroSectionAnimations,
    initStatCounters,
    initPhoneModelAnimations,
};
